import { DialogTrigger, Pressable } from 'react-aria-components';
import { Popover } from '~/shared/components/Popover';
import { Info, PlusCircle, XCircle } from 'lucide-react';
import { useWorkflowTranslations } from '~/features/workflowing/workflows/useWorkflowTranslations';
import { cn } from '~/shared/components/utils';
import { InterventionTypeOptions } from '@generated';
import { Button } from '~/shared/components/buttons/Button';

export function InterventionCard({
	interventionDefinition,
	iconSlot,
	selection,
	children
}: {
	interventionDefinition: {
		title: string;
		definitionText: string;
		documentationWorkflows: string[];
		restrictedToClinicalTypes: { id: number; abbreviation: string }[];
		interventionTypeId?: number;
	};
	iconSlot?: React.ReactNode;
	selection?: {
		selected: boolean;
		onSelect?: (selected: boolean) => void;
	};
	children?: React.ReactNode;
}) {
	const { getTitle } = useWorkflowTranslations();
	const interventionTypeLabel = interventionDefinition.interventionTypeId
		? InterventionTypeOptions.find(x => x.id === interventionDefinition.interventionTypeId)?.label[0]
		: undefined;
	return (
		<div
			className={cn(
				'flex flex-col rounded-lg border border-gray-200 bg-white p-2.5',
				selection?.selected && 'border-primary-500/20 bg-primary-500/5'
			)}
		>
			<div className="flex w-full items-start justify-between">
				<div className="flex items-start gap-4">
					<div className="min-w-0">
						<div className="flex items-center gap-2">
							<p className="text-sm font-semibold leading-snug text-gray-800">
								{interventionDefinition.title}
								{interventionTypeLabel ? ` (${interventionTypeLabel})` : ''}
							</p>
							{interventionDefinition.documentationWorkflows.length > 0 && (
								<DialogTrigger>
									<Pressable>
										<Info role="button" className="size-5 cursor-pointer text-gray-500" />
									</Pressable>

									<Popover showArrow className="max-w-xs p-4">
										<span>
											Visit Types:&nbsp;
											{interventionDefinition.documentationWorkflows
												.map(x => getTitle(x.substring(0, x.lastIndexOf('.'))))
												.join(', ')}
										</span>
									</Popover>
								</DialogTrigger>
							)}
							{iconSlot}
						</div>

						<p className="mt-1 text-xs text-gray-500">{interventionDefinition.definitionText}</p>
						<div className="mt-3 flex flex-wrap items-center gap-1.5">
							{interventionDefinition.restrictedToClinicalTypes.length > 0 ? (
								interventionDefinition.restrictedToClinicalTypes.map(clinicalType => (
									<span key={clinicalType.id} className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
										{clinicalType.abbreviation}
									</span>
								))
							) : (
								<span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">All Roles</span>
							)}
						</div>
					</div>
				</div>
				{!selection ? null : selection.selected ? (
					<Button
						Icon={XCircle}
						size="xs"
						aria-label="Deselect Intervention"
						appearance="outline"
						intent="destructive"
						className="border-none"
						onClick={() => selection.onSelect!(false)}
					/>
				) : (
					<Button
						Icon={PlusCircle}
						size="xs"
						aria-label="Select Intervention"
						appearance="outline"
						intent="success"
						className="border-none"
						onClick={() => selection.onSelect!(true)}
					/>
				)}
			</div>

			{children}
		</div>
	);
}
